import type Hls from "hls.js";
import { resolveOmniMediaUrl } from "../api/client.ts";
import { tokenManager } from "../utils/tokenManager.js";

/**
 * hls.js options for authenticated recording timeline / segment requests.
 *
 * Coalesced recording fragments can be multi-minute MP4s. We give hls.js a
 * larger forward buffer (5 min) and generous fragment timeouts/retries so a
 * single slow MediaMTX `/get` doesn't tear down the whole VOD session.
 */
export function hlsConfigForApiPlayback(lowLatencyMode: boolean): {
  enableWorker: boolean;
  lowLatencyMode: boolean;
  backBufferLength: number;
  maxBufferLength: number;
  maxMaxBufferLength: number;
  maxBufferSize: number;
  startFragPrefetch: boolean;
  manifestLoadingTimeOut: number;
  manifestLoadingMaxRetry: number;
  manifestLoadingRetryDelay: number;
  levelLoadingTimeOut: number;
  levelLoadingMaxRetry: number;
  levelLoadingRetryDelay: number;
  fragLoadingTimeOut: number;
  fragLoadingMaxRetry: number;
  fragLoadingRetryDelay: number;
  xhrSetup: (xhr: XMLHttpRequest) => void;
} {
  return {
    enableWorker: true,
    lowLatencyMode,
    backBufferLength: 60,
    maxBufferLength: 60,
    maxMaxBufferLength: 300,
    maxBufferSize: 120 * 1024 * 1024,
    startFragPrefetch: true,
    manifestLoadingTimeOut: 20_000,
    manifestLoadingMaxRetry: 4,
    manifestLoadingRetryDelay: 500,
    levelLoadingTimeOut: 20_000,
    levelLoadingMaxRetry: 4,
    levelLoadingRetryDelay: 500,
    fragLoadingTimeOut: 60_000,
    fragLoadingMaxRetry: 6,
    fragLoadingRetryDelay: 500,
    xhrSetup(xhr) {
      const token = tokenManager.getToken();
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
    },
  };
}

/** Live HLS settings aligned with wankhede-360 `StreamVideo` (MediaMTX standard HLS). */
export function hlsConfigForLiveStream(): {
  enableWorker: boolean;
  lowLatencyMode: boolean;
  backBufferLength: number;
  maxBufferLength: number;
  maxMaxBufferLength: number;
  maxBufferHole: number;
  startFragPrefetch: boolean;
  nudgeMaxRetry: number;
  liveSyncDuration: number;
  liveMaxLatencyDuration: number;
  liveDurationInfinity: boolean;
  manifestLoadingTimeOut: number;
  manifestLoadingMaxRetry: number;
  manifestLoadingRetryDelay: number;
  levelLoadingTimeOut: number;
  levelLoadingMaxRetry: number;
  levelLoadingRetryDelay: number;
  fragLoadingTimeOut: number;
  fragLoadingMaxRetry: number;
  fragLoadingRetryDelay: number;
} {
  return {
    enableWorker: true,
    lowLatencyMode: false,
    backBufferLength: 30,
    maxBufferLength: 30,
    maxMaxBufferLength: 60,
    maxBufferHole: 0.5,
    startFragPrefetch: true,
    nudgeMaxRetry: 5,
    liveSyncDuration: 3,
    liveMaxLatencyDuration: 18,
    liveDurationInfinity: true,
    manifestLoadingTimeOut: 8_000,
    manifestLoadingMaxRetry: 8,
    manifestLoadingRetryDelay: 300,
    levelLoadingTimeOut: 8_000,
    levelLoadingMaxRetry: 8,
    levelLoadingRetryDelay: 300,
    fragLoadingTimeOut: 12_000,
    fragLoadingMaxRetry: 8,
    fragLoadingRetryDelay: 300,
  };
}

/** Recover from fatal hls.js errors before tearing down the player. */
export function recoverLiveHlsError(
  hls: Hls,
  data: { fatal?: boolean; type?: string },
  state: { networkAttempts: number; mediaAttempts: number },
  limits: { networkMax: number; mediaMax: number } = {
    networkMax: 4,
    mediaMax: 3,
  },
): boolean {
  if (!data.fatal) return true;

  if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
    state.networkAttempts += 1;
    if (state.networkAttempts <= limits.networkMax) {
      hls.startLoad();
      return true;
    }
    return false;
  }

  if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
    state.mediaAttempts += 1;
    if (state.mediaAttempts <= limits.mediaMax) {
      hls.recoverMediaError();
      return true;
    }
    return false;
  }

  return false;
}

/**
 * Resolve HLS playlist URL the same way as wankhede-360 (`resolveOmniMediaUrl`):
 * use the SPA origin in dev so CRA `proxy` serves `/api/streams/hls/...`.
 */
export function resolveStreamPlaybackUrl(url: string): string {
  const raw = String(url ?? "").trim();
  if (!raw) return "";
  return resolveOmniMediaUrl(raw);
}

/** Poll until MediaMTX HLS playlist responds (after relay/start). */
export async function waitForHlsPlaylistReady(
  playlistUrl: string,
  maxMs = 30_000,
): Promise<boolean> {
  const resolved = resolveStreamPlaybackUrl(playlistUrl);
  if (!resolved) return false;

  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(resolved, { method: "GET", cache: "no-store" });
      if (res.ok) {
        const body = await res.text();
        if (body.includes("#EXTM3U")) return true;
      }
    } catch {
      /* relay still starting */
    }
    await new Promise((r) => setTimeout(r, 600));
  }
  return false;
}

export interface LiveHlsPlayback {
  destroy: () => void;
}

function resetVideoElement(video: HTMLVideoElement): void {
  video.pause();
  video.removeAttribute("src");
  video.srcObject = null;
  video.load();
}

/**
 * Attaches a live HLS playlist to a video element via hls.js (same stack as wankhede-360).
 */
export async function startLiveHlsPlayback(
  hlsUrl: string,
  video: HTMLVideoElement,
  onFatalError: (message: string) => void,
): Promise<LiveHlsPlayback> {
  const playlistUrl = resolveOmniMediaUrl(hlsUrl);
  if (!playlistUrl.trim()) {
    throw new Error("No HLS playlist URL");
  }

  const { default: Hls } = await import("hls.js");

  if (Hls.isSupported() && /\.m3u8(\?|$)/i.test(playlistUrl)) {
    const hls: Hls = new Hls(hlsConfigForLiveStream());

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(playlistUrl);
    });
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      void video.play().catch(() => undefined);
    });
    const recovery = { networkAttempts: 0, mediaAttempts: 0 };
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (recoverLiveHlsError(hls, data, recovery)) return;
      if (!data.fatal) return;
      onFatalError(data.error?.message ?? "Failed to load live HLS stream");
    });

    hls.attachMedia(video);

    return {
      destroy: () => {
        hls.destroy();
        resetVideoElement(video);
      },
    };
  }

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = playlistUrl;
    const onError = (): void => onFatalError("Failed to load live HLS stream");
    video.addEventListener("error", onError);
    void video.play().catch(() => undefined);
    return {
      destroy: () => {
        video.removeEventListener("error", onError);
        resetVideoElement(video);
      },
    };
  }

  throw new Error("This browser does not support live HLS playback");
}
